//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { PojoMap } from 'pojo-maps';

import { InstagramShopsItemFilter } from '@apis/instagram-shops/instagramShopsApiModel';
import { IAliasedDateRange } from '@components/date-range-picker/dateRangePickerModels';
import { IInstagramSourceAndCountWithAutocomplete } from '@instagram-account/models/IInstagramAutocompleteResponses';
import { SCHEDULE_ITEM_TASK_NAME } from '@instagram-account/redux/account/instagramAccountModel';
import Cuid from '@models/Cuid';
import { IRightsRequestState } from '@models/IItemMetadata';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import { UniversalSearchSortKey } from '@models/UniversalSearchSortKey';
import { hasPermissionForTask } from '@services/permissions';
import { Normalized } from '@utils/normalize';

export interface IInstagramShopsFeatureUgcState {
  autocompleteResults: IInstagramSourceAndCountWithAutocomplete[];
  dateRange: IAliasedDateRange;
  errors: {
    fetchAutocompleteError?: string;
    fetchItemsError?: string;
    fetchRightsRequestStatusError?: string;
  };
  flags: {
    isInitialDataLoaded: boolean;
    isFetchingAutocomplete: boolean;
    isFetchingInitialItems: boolean;
    isFetchingNextItems: boolean;
    hasFetchedAllItems: boolean;
  };
  itemFilter: InstagramShopsItemFilter;
  items: NormalizedInstagramShopsItems;
  rightsRequestStateByCuid: RightsRequestStateByCuid;
  searchTerms: IInstagramSourceAndCountWithAutocomplete[];
  sort: UniversalSearchSortKey;
  totalItemCount: number;
}

export type NormalizedInstagramShopsItems = Normalized<IMlsMediaItem, Cuid>;

export const INSTAGRAM_SHOPS_ITEM_REQUEST_LIMIT = 50;

export type RightsRequestStateByCuid = PojoMap<Cuid, IRightsRequestState>;

export function isInstagramShopsItemFilter(
  itemFilter: InstagramShopsItemFilter,
): itemFilter is InstagramShopsItemFilter {
  return itemFilter in InstagramShopsItemFilter;
}

export enum InstagramShopsItemStatus {
  Featured = 'Featured',
  NotFeatured = 'NotFeatured',
  RightsNeeded = 'RightsNeeded',
}

// We will use the same user permission as scheduling IG content for UGC feature actions
export const hasInstagramShopsFeatureUgcPermission = () =>
  hasPermissionForTask(SCHEDULE_ITEM_TASK_NAME);
